import React, { useState } from "react";
import { Zap, Gem, Loader2, Disc3, Music4, Radio, Scissors, Layers } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useStudio } from "@/studio/StudioContext";
import { useAuth } from "@/auth/AuthContext";
import { useSubscription } from "@/subscription/SubscriptionContext";
import { formatApiErrorDetail } from "@/auth/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIBES = [
  "Soulful boom-bap with dusty Rhodes",
  "Dark trap with heavy 808s",
  "Smooth neo-soul R&B",
  "G-funk west coast bounce",
  "Amapiano log drums",
  "Jazzy lo-fi chill",
  "Deep house groove",
  "Afrobeat percussion",
];

export default function BeatGenerator() {
  const { generateBeat, extendBeat, loadToDeck, loadToTimeline, setActiveTab, sendToChopper } = useStudio();
  const { requireAuth } = useAuth();
  const { openUpgrade, gen, isPro, bags, buyBag, refresh } = useSubscription();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [engine, setEngine] = useState("fast");
  const [mode, setMode] = useState("loop");
  const [duration, setDuration] = useState(16);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showBags, setShowBags] = useState(false);
  const [extending, setExtending] = useState(false);

  const openModal = () => {
    if (!requireAuth()) return;
    setResult(null);
    setShowBags(false);
    refresh();
    setOpen(true);
  };

  const pickMode = (m) => {
    setMode(m);
    setDuration(m === "track" ? 60 : 16);
  };

  const generate = async (text) => {
    const p = (text || prompt).trim();
    if (!p) {
      toast.error("Describe the beat you want first");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const data = await generateBeat({ prompt: p, engine, duration, mode });
      setResult(data);
      refresh();
      toast.success(`Beat ready — added to your Crate`);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 402) {
        if (!isPro) {
          setOpen(false);
          openUpgrade(detail);
        } else {
          // Pro user out of Master beats → surface the Bags right here
          setShowBags(true);
          toast.error(detail || "Out of Master beats — grab a Bag to keep going");
        }
      } else {
        toast.error(detail || "Could not generate a beat");
      }
    }
    setBusy(false);
  };

  const purchaseBag = async (key) => {
    try {
      await buyBag(key);
    } catch (_) {
      toast.error("Could not start checkout, please try again");
    }
  };

  const toDeck = async (deckId) => {
    if (!result) return;
    try {
      await loadToDeck(deckId, { url: result.url, name: result.name });
      toast.success(`Cued on ${deckId === "deckA" ? "Deck A" : "Deck B"}`);
      setActiveTab("decks");
      setOpen(false);
    } catch (_) {
      toast.error("Could not load onto the deck");
    }
  };

  const chopIt = () => {
    if (!result) return;
    sendToChopper({ url: result.url, name: result.name });
    setOpen(false);
    toast.success("Loaded into the Sample Chopper — slice it & play it on the pads");
  };

  const doExtend = async () => {
    if (!result) return;
    setExtending(true);
    try {
      const data = await extendBeat({ mediaId: result.id, addSeconds: 30 });
      setResult(data);
      refresh();
      toast.success(`Extended to ${data.duration}s — seamless continuation`);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (e.response?.status === 402) {
        if (!isPro) { setOpen(false); openUpgrade(detail); }
        else { setShowBags(true); toast.error(detail || "Out of Master beats"); }
      } else {
        toast.error(detail || "Could not extend the beat");
      }
    } finally {
      setExtending(false);
    }
  };

  const toTimeline = async () => {
    if (!result) return;
    try {
      await loadToTimeline({ url: result.url, name: result.name });
      setOpen(false);
      toast.success("Dropped on the Timeline — hit ▶ Play, then jump to Beats & Keys and build over it");
    } catch (_) {
      toast.error("Could not send to the timeline");
    }
  };

  return (
    <>
      <button
        data-testid="beat-lab-btn"
        onClick={openModal}
        className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-bold text-black transition-all hover:scale-[1.03]"
        style={{
          background: "linear-gradient(120deg,#ffb800,#ff007f)",
          boxShadow: "0 0 14px rgba(255,120,0,0.45)",
        }}
        title="Generate a real, studio-quality beat with AI"
      >
        <Gem size={14} /> Beat Lab
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-white max-w-lg" data-testid="beat-lab-modal">
          <DialogHeader>
            <DialogTitle className="font-display tracking-widest flex items-center gap-2">
              <Gem size={18} className="text-[#ffb800]" />
              BEAT LAB
            </DialogTitle>
            <DialogDescription className="text-neutral-500 text-xs">
              Generate a real, editable beat — then chop it, load it to a deck, or master it. Not a jukebox. A studio.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 mt-1">
            {gen && (
              <div data-testid="beat-usage" className="flex items-center justify-between gap-2 px-3 py-2 rounded-sm bg-[#080808] border border-[#1e1e1e] text-[10px]">
                <span className="flex items-center gap-1.5 text-[#00e5ff]">
                  <Zap size={11} /> Fast{" "}
                  <b className="font-mono-data">
                    {gen.fast_limit == null ? "∞" : `${Math.max(0, gen.fast_limit - gen.fast_used)}/${gen.fast_limit}`}
                  </b>
                </span>
                <span className="flex items-center gap-1.5 text-[#ffb800]">
                  <Gem size={11} /> Master{" "}
                  <b className="font-mono-data">
                    {gen.master_limit == null ? "∞" : `${Math.max(0, gen.master_limit - gen.master_used)}/${gen.master_limit}`}
                  </b>
                  {gen.master_credits > 0 && <span className="text-[#ff007f]">+{gen.master_credits} bag</span>}
                </span>
                <button
                  data-testid="beat-open-bags"
                  onClick={() => setShowBags((s) => !s)}
                  className="text-neutral-400 hover:text-[#ffb800] transition-colors underline"
                >
                  Get more
                </button>
              </div>
            )}
            <input
              data-testid="beat-prompt-input"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
              placeholder="e.g. R&B beat with a grand piano intro and a saxophone solo — name the instruments you want"
              disabled={busy}
              className="bg-[#050505] border border-[#262626] rounded-sm px-3 py-2.5 text-sm outline-none focus:border-[#ffb800] transition-colors"
            />

            <div className="flex flex-wrap gap-1.5">
              {VIBES.map((v) => (
                <button
                  key={v}
                  data-testid={`beat-vibe-${v.split(" ")[0].toLowerCase()}`}
                  disabled={busy}
                  onClick={() => setPrompt(v)}
                  className="text-[11px] px-2.5 py-1 rounded-full border border-[#333] text-neutral-300 hover:border-[#ffb800] hover:text-white transition-colors disabled:opacity-40"
                >
                  {v}
                </button>
              ))}
            </div>

            {/* Engine selector */}
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="beat-engine-fast"
                onClick={() => setEngine("fast")}
                disabled={busy}
                className="flex flex-col items-start gap-0.5 p-3 rounded-sm border text-left transition-colors"
                style={engine === "fast"
                  ? { borderColor: "#00e5ff", background: "rgba(0,229,255,0.08)" }
                  : { borderColor: "#2a2a2a" }}
              >
                <span className="flex items-center gap-1.5 text-xs font-bold" style={{ color: "#00e5ff" }}>
                  <Zap size={13} /> Fast · Quick Loop
                </span>
                <span className="text-[10px] text-neutral-500">Free · a quick beat to build on</span>
              </button>
              <button
                data-testid="beat-engine-master"
                onClick={() => setEngine("master")}
                disabled={busy}
                className="flex flex-col items-start gap-0.5 p-3 rounded-sm border text-left transition-colors"
                style={engine === "master"
                  ? { borderColor: "#ffb800", background: "rgba(255,184,0,0.08)" }
                  : { borderColor: "#2a2a2a" }}
              >
                <span className="flex items-center gap-1.5 text-xs font-bold" style={{ color: "#ffb800" }}>
                  <Gem size={13} /> Master · Studio Track
                </span>
                <span className="text-[10px] text-neutral-500">Pro — full studio-quality, sellable</span>
              </button>
            </div>

            {/* Mode + duration */}
            <div className="flex items-center gap-3">
              <div className="flex rounded-sm border border-[#2a2a2a] overflow-hidden">
                <button
                  data-testid="beat-mode-loop"
                  onClick={() => pickMode("loop")}
                  disabled={busy}
                  className="px-3 py-1.5 text-[11px] font-bold transition-colors flex items-center gap-1.5"
                  style={mode === "loop" ? { background: "#ff007f", color: "#fff" } : { color: "#737373" }}
                >
                  <Radio size={12} /> Loop
                </button>
                <button
                  data-testid="beat-mode-track"
                  onClick={() => pickMode("track")}
                  disabled={busy}
                  className="px-3 py-1.5 text-[11px] font-bold transition-colors flex items-center gap-1.5"
                  style={mode === "track" ? { background: "#ff007f", color: "#fff" } : { color: "#737373" }}
                >
                  <Music4 size={12} /> Full Track
                </button>
              </div>
              <div className="flex-1 flex items-center gap-2">
                <span className="hw-label text-[9px] text-neutral-400 shrink-0">Length</span>
                <input
                  data-testid="beat-duration"
                  type="range"
                  min={mode === "track" ? 30 : 5}
                  max={mode === "track" ? 180 : 30}
                  step={1}
                  value={duration}
                  onChange={(e) => setDuration(Number(e.target.value))}
                  disabled={busy}
                  className="flex-1"
                />
                <span className="font-mono-data text-[11px] text-[#ffb800] w-9 text-right">{duration}s</span>
              </div>
            </div>

            <button
              data-testid="beat-generate-btn"
              onClick={() => generate()}
              disabled={busy}
              className="flex items-center justify-center gap-2 rounded-sm py-2.5 text-sm font-bold text-black transition-colors disabled:opacity-50"
              style={{ background: "linear-gradient(120deg,#ffb800,#ff007f)" }}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Gem size={15} />}
              {busy
                ? engine === "master" ? "Rendering studio-grade beat…" : "Cooking up your beat…"
                : `Generate ${engine === "master" ? "Master" : "Fast"} Beat`}
            </button>

            {showBags && (
              <div data-testid="beat-bags" className="flex flex-col gap-2 p-3 rounded-sm border border-[#ffb80044] bg-[#ffb8000a]">
                <span className="hw-label text-[10px] text-[#ffb800] flex items-center gap-1.5">
                  <Gem size={12} /> Master Beat Bags — never expire
                </span>
                <div className="grid grid-cols-3 gap-2">
                  {bags.map((b) => (
                    <button
                      key={b.lookup_key}
                      data-testid={`bag-${b.lookup_key}`}
                      onClick={() => purchaseBag(b.lookup_key)}
                      className="flex flex-col items-center gap-0.5 p-2.5 rounded-sm border border-[#2a2a2a] hover:border-[#ffb800] transition-colors"
                    >
                      <span className="text-[11px] font-bold text-white">{b.credits}</span>
                      <span className="text-[9px] text-neutral-500">beats</span>
                      <span className="text-[11px] font-bold text-[#ffb800]">{b.price}</span>
                    </button>
                  ))}
                </div>
                <span className="text-[9px] text-neutral-500 text-center">Secure checkout via Stripe. Credits stack on top of your monthly Master beats.</span>
              </div>
            )}

            {result && (
              <div data-testid="beat-result" className="flex flex-col gap-2 p-3 rounded-sm border border-[#2a2a2a] bg-[#0a0a0a]">
                <span className="hw-label text-[10px] text-[#ffb800] flex items-center gap-1.5">
                  <Gem size={12} /> {result.name}
                </span>
                <audio controls src={`${BACKEND_URL}${result.url}`} className="w-full h-9" data-testid="beat-result-audio" />
                {result.engine === "master" && result.duration < 180 && (
                  <button
                    data-testid="beat-result-extend"
                    onClick={doExtend}
                    disabled={extending}
                    className="w-full flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-bold border border-[#ffb800] text-[#ffb800] hover:bg-[#ffb80014] transition-colors disabled:opacity-50"
                  >
                    {extending ? <Loader2 size={13} className="animate-spin" /> : <Gem size={13} />}
                    {extending ? `Extending to ${Math.min(180, result.duration + 30)}s…` : `Extend +30s (seamless · ${result.duration}s → ${Math.min(180, result.duration + 30)}s)`}
                  </button>
                )}
                <button
                  data-testid="beat-result-timeline"
                  onClick={toTimeline}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-bold text-black transition-transform hover:scale-[1.02]"
                  style={{ background: "linear-gradient(120deg,#00e5ff,#7c3aed)" }}
                >
                  <Layers size={14} /> Build On It — Send to Timeline
                </button>
                <button
                  data-testid="beat-result-chop"
                  onClick={chopIt}
                  className="w-full flex items-center justify-center gap-1.5 py-2 rounded-sm text-xs font-bold text-black transition-transform hover:scale-[1.02]"
                  style={{ background: "linear-gradient(120deg,#ffb800,#ff007f)" }}
                >
                  <Scissors size={14} /> Chop &amp; Play in Studio
                </button>
                <div className="flex items-center gap-2">
                  <button
                    data-testid="beat-result-deck-a"
                    onClick={() => toDeck("deckA")}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-[11px] font-bold border transition-colors"
                    style={{ borderColor: "#00e5ff55", color: "#00e5ff" }}
                  >
                    <Disc3 size={13} /> Deck A
                  </button>
                  <button
                    data-testid="beat-result-deck-b"
                    onClick={() => toDeck("deckB")}
                    className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-sm text-[11px] font-bold border transition-colors"
                    style={{ borderColor: "#ff007f55", color: "#ff007f" }}
                  >
                    <Disc3 size={13} /> Deck B
                  </button>
                </div>
                <span className="text-[10px] text-neutral-500 text-center">
                  Also saved to your Crate. "Build On It" drops the beat on a Timeline track — press Play and layer your own drums &amp; keys over it in time.
                </span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
